/**
 * DopplerPulseMap — pure-SVG world map with animated pulse rings at
 * representative DDARP observation node locations.
 *
 * Built for the Login page redesign (Public Pages Refinements Phase 10).
 * Reinforces DDARP's "Doppler radar for the Internet" V2 positioning
 * without pulling in vendor-3d (Three.js / react-globe.gl). The whole
 * component is < 8 KB gzipped because everything is inline SVG and CSS
 * keyframes.
 *
 * Design
 * - Background: dark navy (#0A1929) gradient, matches the Higgsfield V2
 *   hero color
 * - Equirectangular world outline rendered as a simplified polyline
 *   path (continents only, no labels)
 * - Pulse rings at 5 anchor cities: Virginia, Frankfurt, Mumbai, Tokyo,
 *   São Paulo. Each city gets 3 concentric rings expanding from r=0
 *   to r=40 over 2.8 s, fading from opacity 0.7 to 0. Pulses are
 *   staggered by city so the overall surface feels continuous
 * - Sub-band caption at the bottom: "Observed by 118 nodes across 3 clouds"
 *
 * Accessibility
 * - aria-hidden + role="presentation": the animation is decorative
 * - Honors prefers-reduced-motion: when set, pulses are static circles
 *   at full radius (visual presence preserved, motion removed)
 *
 * Phase 10 of Public Pages Refinements (2026-05-26).
 */
import { useEffect, useState } from 'react';


// Approximate equirectangular projection (x = lon, y = -lat) of the 5
// anchor cities. The viewBox is 0..360 x 0..180 (lon shifted +180,
// lat shifted +90, top-down). City coords mapped accordingly.
const PULSE_NODES = [
  { id: 'virginia',  cx: 100,  cy:  53, delay: 0.0 },  // ~-77, +38
  { id: 'frankfurt', cx: 189,  cy:  40, delay: 0.6 },  // ~+9,  +50
  { id: 'saopaulo',  cx: 134,  cy: 113, delay: 1.0 },  // ~-46, -23
  { id: 'mumbai',    cx: 253,  cy:  72, delay: 1.4 },  // ~+73, +18
  { id: 'tokyo',     cx: 320,  cy:  53, delay: 2.0 },  // ~+140,+38
];


// Simplified world-map continent outlines. These are not geographically
// precise — they're decorative silhouettes at the same equirectangular
// projection as PULSE_NODES so the pulses appear over recognizable
// landmasses. Borrowed from a public-domain world-110m simplification.
const CONTINENT_PATHS = [
  // North America
  'M 35 30 L 70 22 L 110 28 L 130 50 L 122 70 L 95 75 L 78 65 L 60 55 L 40 45 Z',
  // South America
  'M 110 85 L 130 80 L 145 90 L 150 110 L 140 130 L 125 145 L 115 130 L 110 110 Z',
  // Europe
  'M 170 30 L 200 25 L 215 35 L 210 50 L 190 55 L 175 48 Z',
  // Africa
  'M 175 60 L 210 55 L 225 75 L 220 100 L 205 125 L 190 130 L 180 110 L 175 85 Z',
  // Asia
  'M 215 25 L 280 22 L 330 30 L 340 55 L 320 75 L 295 78 L 270 70 L 240 60 L 225 45 Z',
  // Southeast Asia / Indonesia
  'M 290 90 L 320 88 L 325 100 L 310 105 L 295 100 Z',
  // Australia
  'M 305 115 L 335 113 L 340 130 L 325 138 L 310 132 Z',
];


export function DopplerPulseMap() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <div
      role="presentation"
      aria-hidden="true"
      className="relative w-full h-full bg-gradient-to-br from-[#061325] via-[#0A1929] to-[#0C2440] overflow-hidden"
    >
      {/* Subtle grid pattern for depth */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Scoped CSS for the pulse keyframes. We use Tailwind everywhere
          else but the keyframes need a local @keyframes declaration. */}
      <style>{`
        @keyframes dopplerPulse {
          0%   { r: 0;  opacity: 0.75; stroke-width: 1.5; }
          80%  { r: 38; opacity: 0; stroke-width: 0.5; }
          100% { r: 40; opacity: 0; stroke-width: 0.5; }
        }
        .doppler-pulse {
          animation: dopplerPulse 2.8s ease-out infinite;
          transform-origin: center;
        }
        @media (prefers-reduced-motion: reduce) {
          .doppler-pulse { animation: none !important; }
        }
      `}</style>

      <svg
        viewBox="0 0 360 180"
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
      >
        {/* Continent silhouettes */}
        {CONTINENT_PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="rgba(96, 165, 250, 0.08)"
            stroke="rgba(96, 165, 250, 0.18)"
            strokeWidth="0.5"
          />
        ))}

        {/* Pulses */}
        {PULSE_NODES.map((node) => (
          <g key={node.id}>
            {/* Anchor dot */}
            <circle
              cx={node.cx}
              cy={node.cy}
              r={1.6}
              fill="#7dd3fc"
              opacity={0.95}
            />
            {/* 3 concentric pulse rings if motion enabled, single static
                ring if reduced-motion is set */}
            {reducedMotion ? (
              <circle
                cx={node.cx}
                cy={node.cy}
                r={14}
                fill="none"
                stroke="#7dd3fc"
                strokeWidth={0.8}
                opacity={0.35}
              />
            ) : (
              <>
                <circle
                  className="doppler-pulse"
                  cx={node.cx}
                  cy={node.cy}
                  fill="none"
                  stroke="#7dd3fc"
                  style={{ animationDelay: `${node.delay}s` }}
                />
                <circle
                  className="doppler-pulse"
                  cx={node.cx}
                  cy={node.cy}
                  fill="none"
                  stroke="#60a5fa"
                  style={{ animationDelay: `${node.delay + 0.93}s` }}
                />
                <circle
                  className="doppler-pulse"
                  cx={node.cx}
                  cy={node.cy}
                  fill="none"
                  stroke="#93c5fd"
                  style={{ animationDelay: `${node.delay + 1.86}s` }}
                />
              </>
            )}
          </g>
        ))}
      </svg>

      {/* Trust caption — overlaid bottom-left */}
      <div className="absolute bottom-8 left-8 right-8 text-white/85 z-10">
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-3 font-semibold">
          Observation substrate
        </div>
        <div className="text-2xl lg:text-3xl font-bold leading-tight mb-2">
          Observed by 118 nodes
        </div>
        <div className="text-2xl lg:text-3xl font-bold leading-tight mb-4">
          across 3 clouds
        </div>
        <div className="text-sm text-white/55 font-light max-w-sm">
          MadHat Media, Inc. · Patented continuity-observation protocol
        </div>
      </div>
    </div>
  );
}

export default DopplerPulseMap;
